import { Injectable, inject } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { WarehouseControllerService } from '@app/api/stock-service/api/warehouseController.service';
import { SearchOrderEsParam } from '@app/api/order-service/model/searchOrderEsParam';
import { PageInfoOrderPageVo } from '@app/api/order-service/model/pageInfoOrderPageVo';
import { PhysicalShopShowDTO } from '@app/api/channel-service/model/physicalShopShowDTO';
import { PageRequestDTO } from '@app/api/channel-service/model/pageRequestDTO';
import { OrderDTO } from '@app/api/stock-service/model/orderDTO';
import { DistanceWarehouseResVO } from '@app/api/stock-service/model/distanceWarehouseResVO';
import { OrderChangeHistorySearchDTO } from '@app/api/order-service/model/orderChangeHistorySearchDTO';
import { PageInfoOrderChangeHistory } from '@app/api/order-service/model/pageInfoOrderChangeHistory';
import {
  O2OOrder,
  O2OSearchParams,
  O2OAuditResult,
  O2OAuditAction,
} from '@models/o2o-order';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import {
  mapPageContentToPaginatedPage,
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';

export interface SalesStoreOption {
  label: string;
  value: number;
  spuId?: number;
  spuCode?: string;
  physicalWarehouseId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class O2OOrderService {
  private readonly orderService = inject(OrderControllerService);
  private readonly physicalShopService = inject(PhysicalShopControllerService);
  private readonly warehouseService = inject(WarehouseControllerService);
  private readonly salesStoreCache = new Map<number, SalesStoreOption>();

  searchOrders(params: O2OSearchParams): Observable<O2OAuditResult> {
    // Convert O2O search params to API search params
    const searchParams: SearchOrderEsParam = {
      page: (params.page || 1) - 1, // API uses 0-based indexing
      size: params.pageSize || 20,
      businessType: 'O2O', // Filter for O2O orders only
      orderNo: params.orderNo,
      buyerNick: params.buyerNickname,
      receiverName: params.recipientName,
      mobile: params.recipientPhone,
      buyerRemarks: params.buyerRemarks,
      serviceRemarks: params.merchantRemarks,
      isGift: params.hasGift,
      orderStartTime: params.thirdPartyOrderTimeRange?.[0],
      orderEndTime: params.thirdPartyOrderTimeRange?.[1],
      payStartTime: params.paymentTimeRange?.[0],
      payEndTime: params.paymentTimeRange?.[1],
      creationStartDate: params.internalCreateTimeRange?.[0],
      creationEndDate: params.internalCreateTimeRange?.[1],
      channel: params.salesChannel,
      storeIdList: params.salesStore ? [Number(params.salesStore)] : undefined,
      tags: undefined, // tags not available in O2OSearchParams
      // Map audit status to order status (backend expects: "0"=incomplete, "1"=completed, "2"=closed, "3"=exception, undefined=all)
      orderStatus: this.mapAuditStatusToOrderStatus(params.auditStatus),
    };

    return this.orderService
      .orderPage1(searchParams, 'body', false, {
        httpHeaderAccept: 'application/json',
      } as any)
      .pipe(
        // Transform API response to O2O format
        map((response: PageInfoOrderPageVo) => {
          const o2oOrders: O2OOrder[] = (response.pageContent || []).map(
            (order, index) => ({
              id: order.id || 0,
              serialNo: index + 1,
              thirdPartyOrderNo: order.outerOrderNo || '',
              internalOrderNo: order.innerOrderNo || '',
              tags: order.tagsList || [],
              auditStatus: order.orderStatus as
                | 'pending_first'
                | 'pending_second'
                | 'rejected'
                | 'approved',
              salesStore: order.storeName || '',
              salesChannel: order.channel || '',
              salesEmployee: order.salesEmployeeRealName || '',
              pickupStore: order.takingDeliveryStoreName || '',
              orderSource: order.actionType || '',
              productLineCount: order.goodsQuatity || 0,
              buyerNickname: order.buyerNick || '',
              buyerRemarks: order.buyerRemarks || '',
              merchantRemarks: order.orderRemarks || '',
              pickupMethod: order.takingDeliveryWay || '',
              recipientName: order.receiverName || '',
              recipientPhone: order.mobile || '',
              recipientAddress: order.address || '',
              totalAmount: order.totalAmount || 0,
              buyerPayable: order.buyerPay || 0,
              totalCost: order.costPrice || 0,
              thirdPartyOrderTime: order.orderTime || '',
              paymentTime: order.payTime || '',
              internalCreateTime: order.creationDate || '',
              hasGift: order.isGift || false,
            }),
          );

          return {
            success: true,
            orders: o2oOrders,
            total: response.totalElements || 0,
          };
        }),
      );
  }

  getSalesStoresPage(
    params: PaginatedDropdownLoadParams & {
      status?: boolean;
      selfPickupFlag?: boolean;
    },
  ): Observable<PaginatedOptionsPage<number>> {
    const pageRequest: PageRequestDTO = {
      page: params.page - 1,
      size: params.size,
    };

    const physicalShopDTO: PhysicalShopShowDTO = {
      name: params.searchText || '',
      typeCode: '',
      manager: '',
      status: params.status ?? true,
      selfPickupFlag: params.selfPickupFlag,
      deleteFlag: false,
      pageRequest,
    };

    return this.physicalShopService.showPage(physicalShopDTO, 'body', false).pipe(
      switchMap((response: unknown) => {
        if (response instanceof Blob) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((response: Record<string, unknown>) => {
        const content = (response['pageContent'] ?? []) as Record<
          string,
          unknown
        >[];
        const stores: SalesStoreOption[] = content
          .filter((store) => store['name'])
          .map((store) => ({
            label: String(store['name']),
            value: Number(store['id']),
            spuId: Number(store['id']),
            spuCode: store['code'] as string | undefined,
            physicalWarehouseId: store['physicalWarehouseId'] as
              | number
              | undefined,
          }));

        stores.forEach((store) => this.salesStoreCache.set(store.value, store));

        return mapPageContentToPaginatedPage(
          {
            pageContent: stores,
            totalElements: response['totalElements'] as number | undefined,
            hasNextPage: response['hasNextPage'] as boolean | undefined,
          },
          (store) => ({
            label: store.label,
            value: store.value,
          }),
        );
      }),
      catchError((error) => {
        console.error('Error loading sales stores page:', error);
        return of({ options: [], pageContentLength: 0 });
      }),
    );
  }

  getSalesStoreById(storeId: number): SalesStoreOption | undefined {
    return this.salesStoreCache.get(storeId);
  }

  getSalesStores(
    status?: boolean,
    selfPickupFlag?: boolean,
  ): Observable<SalesStoreOption[]> {
    const pageRequest: PageRequestDTO = {
      page: 0,
      size: 100,
    };

    const physicalShopDTO: PhysicalShopShowDTO = {
      name: '',
      typeCode: '',
      manager: '',
      status: status,
      selfPickupFlag: selfPickupFlag,
      deleteFlag: false,
      pageRequest: pageRequest,
    };

    return this.physicalShopService
      .showPage(physicalShopDTO, 'body', false)
      .pipe(
        map(async (response: any) => {
          try {
            let data: any[];

            // Handle blob response
            if (response instanceof Blob) {
              const jsonData = await BlobConverterUtil.convertToJson(response);
              data = jsonData?.pageContent || [];
            } else {
              data = response?.pageContent || [];
            }

            return data
              .filter((store: Record<string, unknown>) => store['name'])
              .map((store: Record<string, unknown>) => ({
                label: String(store['name']),
                value: Number(store['id']),
                spuId: Number(store['id']),
                spuCode: store['code'] as string | undefined,
                physicalWarehouseId: store['physicalWarehouseId'] as
                  | number
                  | undefined,
              }));
          } catch (error) {
            console.error('Error processing physical store response:', error);
            return [];
          }
        }),
        switchMap((promise) => from(promise)),
        catchError((error) => {
          console.error('Error loading stores:', error);
          return of([]);
        }),
      );
  }

  /**
   * Get nearest fulfill warehouse based on order items and receiver info
   */
  getNearestFulfillWarehouse(
    orderDTO: OrderDTO,
  ): Observable<DistanceWarehouseResVO[]> {
    return this.warehouseService
      .getNearestFulfillWarehouse(orderDTO, 'body', false)
      .pipe(
        switchMap((response: any) => {
          if (response instanceof Blob) {
            return new Observable<DistanceWarehouseResVO[]>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(Array.isArray(json) ? json : []);
                  observer.complete();
                } catch (error) {
                  console.error('Error parsing Blob to JSON:', error);
                  observer.error(error);
                }
              };
              reader.onerror = (error) => {
                console.error('Error reading Blob:', error);
                observer.error(error);
              };
              reader.readAsText(response);
            });
          }
          if (response instanceof HttpResponse) {
            return of(response.body || []);
          }
          return of(Array.isArray(response) ? response : []);
        }),
        catchError((error) => {
          console.error('Error getting nearest fulfill warehouse:', error);
          return of([]);
        }),
      );
  }

  /**
   * Map audit status to backend order status number
   * Backend constants:
   * - NO_COMPLETE = "0"
   * - COMPLETE = "1"
   * - CLOSE = "2"
   * - EXCEPTION = "3"
   * If status is 'all', return undefined to exclude from payload
   */
  private mapAuditStatusToOrderStatus(
    auditStatus?: 'all' | 'incomplete' | 'completed' | 'closed' | 'exception',
  ): string | undefined {
    if (!auditStatus || auditStatus === 'all') {
      return undefined; // Don't include orderStatus in payload when 'all' is selected
    }

    const statusMap: Record<string, string> = {
      incomplete: '0', // NO_COMPLETE
      completed: '1', // COMPLETE
      closed: '2', // CLOSE
      exception: '3', // EXCEPTION
    };

    return statusMap[auditStatus];
  }

  auditOrders(auditAction: O2OAuditAction): Observable<boolean> {
    // Mock implementation - replace with actual API call
    return of(true).pipe(
      map(() => {
        return true;
      }),
      catchError((error) => {
        console.error('Error auditing orders:', error);
        return of(false);
      }),
    );
  }

  /**
   * Load append-only order change history (RBA-2723 delivery-retry audit + other status/field changes).
   */
  listOrderChangeHistory(params: {
    orderId?: number;
    innerOrderNo?: string;
    outerOrderNo?: string;
    fieldName?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    size?: number;
  }): Observable<PageInfoOrderChangeHistory> {
    const body: OrderChangeHistorySearchDTO = {
      orderId: params.orderId,
      innerOrderNo: params.innerOrderNo,
      outerOrderNo: params.outerOrderNo,
      fieldName: params.fieldName,
      fromDate: params.fromDate,
      toDate: params.toDate,
      page: params.page ?? 0,
      size: params.size ?? 20,
      ascending: false,
    };

    return this.orderService
      .listOrderChangeHistory(body, 'body', false, {
        httpHeaderAccept: 'application/json',
      } as any)
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(
              BlobConverterUtil.convertToJson(
                response,
              ) as Promise<PageInfoOrderChangeHistory>,
            );
          }
          return of(response as PageInfoOrderChangeHistory);
        }),
      );
  }
}
